// ============================================================================
// VEHICLES RDB SUBSCRIPTION
// ============================================================================
// RDB transport for vehicle positions + trip tracking. Writes into the SAME
// stores the rest of the app already consumes:
//
//   • Positions  → vehiclesSocketStore (read by Sidebar, MapView, VehicleCard)
//   • Trips snap → tripTrackingStore.updateFromEvent (read by VehicleCard)
//   • Trip evts  → tripTrackingStore.updateFromEvent (events history)
//
// Socket-only methods (getLastPosition, getAddress, getTripStatus) are NOT
// called: RDB delivers full snapshots on subscribe. Reconnect/heartbeats are
// handled by Firebase RDB itself.
// ============================================================================

import { useEffect, useRef, useMemo, useCallback } from "react";
import {
  ref,
  onValue,
  off,
  query,
  limitToLast,
  type DatabaseReference,
} from "firebase/database";
import { database } from "../firebaseConfig";
import { useVehiclesSocketStore } from "../store/admin/vehiclesSocketStore";
import type { PositionFromSocket } from "../store/admin/vehiclesSocketStore";
import { useTripTrackingStore } from "../store/admin/tripTrackingStore";
import { useVehicleFootprintTrackingStore } from "../store/admin/vehicleFootprintTrackingStore";
import { useCompaniesStore } from "../store/admin/companiesStore";
import { ADMIN_RESELLER_ROLE_ID } from "./useIsReseller";
import { useGroupsStore } from "../store/admin/groupsStore";
import { useDeviceEventsStore } from "../store/admin/deviceEventsStore";
import type { TripTrackingEvent } from "../socket";
import api from "../utils/api";
import { driverDisplayName } from "../utils/driverName";

// Códigos que disparan SOS en la VehicleCard (mismo set que en el backend
// `eventAlertService.SOS_EVENT_CODES`). Si llega un position con este
// event_code, derivamos `params.panic` para que prenda el icono SOS sin
// depender del listener separado de deviceEvents.
const SOS_EVENT_CODES = new Set<number>([5]);

interface DeviceFromAPI {
  id: string;
  fullId?: string;
  identifier?: string;
  model?: string;
  status_id?: string;
  type?: string;
  company_id?: string;
  companyId?: string;
  vehicleId?: string;
  vehicle_id?: string;
  remolque_id?: string;
  serial?: string;
  sim_card?: string;
  createdAt?: { _seconds: number; _nanoseconds: number };
  updatedAt?: { _seconds: number; _nanoseconds: number };
  vehicle?: {
    id?: string;
    brand?: string;
    model?: string;
    year?: number;
    plates?: string;
    plate?: string;
    color?: string;
    name?: string;
    type?: string;
  };
  driver?: {
    id?: string;
    firstName?: string;
    lastName?: string;
    license_id?: string;
    photo?: string;
    experience?: string;
    phone?: string;
    email?: string;
  };
  group?: string;
  icon?: string;
  imei?: string;
  lastPosition?: {
    lat?: number;
    lon?: number;
    speed?: number | null;
    heading?: number | null;
    timeStamp?: number;
    altitude?: number | null;
    accuracy?: number | null;
    battery?: number | null;
    ignition?: boolean;
    event_code?: number;
    params?: {
      engine_status?: number;
      event_code?: number;
      odo_km?: number;
      fuel?: number | null;
      engine_hours?: number | null;
    };
  };
}

interface RdbPosition {
  device_id: string;
  vehicle_id: string | null;
  remolque_id: string | null;
  company_id: string;
  imei: string;
  lat: number;
  lon: number;
  speed: number | null;
  heading: number | null;
  battery: number | null;
  altitude: number | null;
  ignition: boolean | null;
  event_code: number | null;
  timeStamp: number;
  date: string;
  params: {
    panic?: number | null;
    engine_status: number | null;
    odo_km: number | null;
    fuel: number | null;
    engine_hours: number | null;
    dallas_temperature?: number | null;
  };
  updatedAt: number;
}

// Payload publicado por el backend en `deviceEvents/{companyId}/{deviceId}/{pushId}`
// (eventAlertService.recordDeviceEvent). Es un superset del SocketEvent que
// consume `useDeviceEventsStore.addEvent`.
interface RdbDeviceEvent {
  device_id: string;
  company_id: string | null;
  vehicle_id: string | null;
  remolque_id: string | null;
  imei: string | null;
  type: string | null;
  event_type: string | null;
  event_code: number | null;
  event_label?: string | null;
  lat: number | null;
  lon: number | null;
  timeStamp: number | null;
  gps_sat: number | null;
  engine_status: number | null;
  odo_km: number | null;
  rssi: number | null;
  sim: string | null;
  vehicle_name?: string | null;
  device_name?: string | null;
  driver_name?: string | null;
  is_alert?: boolean;
  is_sos?: boolean;
}

export const fetchDevicesFromAPI = async (companyId?: string) => {
  try {
    const response = await api.get("/deviceList", {
      params: { limit: 200, page: 1 },
      headers: { "x-company-id": companyId || "" },
    });
    const data = response.data.data;

    const normalizedData = (data || []).map((device: DeviceFromAPI) => ({
      id: device.id,
      fullId: device.fullId || device.identifier,
      identifier: device.identifier,
      companyId: device.companyId || device.company_id || companyId || "",
      vehicle_id: device.vehicle_id || device.vehicleId,
      remolque_id: device.remolque_id,
      model: device.model,
      status_id: device.status_id,
      type: device.type,
      serial: device.serial,
      sim_card: device.sim_card,
      imei: device.imei || device.identifier,
      vehicle: device.vehicle
        ? {
            ...device.vehicle,
            plates: device.vehicle.plates || device.vehicle.plate,
          }
        : undefined,
      driver: device.driver
        ? {
            id: device.driver.id,
            name: driverDisplayName(device.driver) || "Sin asignar",
            licenseId: device.driver.license_id || "",
            photo: device.driver.photo || "",
            experience: device.driver.experience || "",
            phone: device.driver.phone || "",
            email: device.driver.email,
          }
        : undefined,
      group: device.group,
      icon: device.icon,
      lastPosition: device.lastPosition,
    }));

    return normalizedData;
  } catch (error) {
    console.error("Error fetching devices:", error);
    return [];
  }
};

const rdbPositionToSocketFormat = (pos: RdbPosition): PositionFromSocket => {
  // Derivar panic desde event_code para prender el SOS en VehicleCard.
  // El backend (`historyPositionsController.sendPosition`) también escribe
  // `params.panic` cuando el event_code es de SOS — preferimos lo que venga
  // del backend si llega, y caemos a la derivación local si no.
  const codeNum = typeof pos.event_code === "number" ? pos.event_code : null;
  const derivedPanic =
    codeNum != null && SOS_EVENT_CODES.has(codeNum) ? codeNum : null;

  return {
    device_id: pos.device_id,
    company_id: pos.company_id,
    vehicle_id: pos.vehicle_id ?? "",
    remolque_id: pos.remolque_id ?? "",
    imei: pos.imei ?? "",
    timeStamp: pos.timeStamp,
    lat: pos.lat,
    long: pos.lon,
    heading: pos.heading ?? 0,
    battery: pos.battery ?? null,
    altitude: pos.altitude ?? 0,
    event_code: pos.event_code ?? 0,
    speed: pos.speed ?? null,
    ignition: pos.ignition ?? null,
    params: {
      panic: pos.params?.panic ?? derivedPanic,
      engine_status: pos.params?.engine_status ?? null,
      event_code: pos.event_code ?? 0,
      odo_km: pos.params?.odo_km ?? null,
      provider: "",
      fuel: pos.params?.fuel ?? null,
      engine_hours: pos.params?.engine_hours ?? null,
      dallas_temperature: pos.params?.dallas_temperature ?? undefined,
    },
  };
};

export const useVehiclesRdbSubscription = () => {
  const vehicles = useVehiclesSocketStore((state) => state.vehicles);
  const loading = useVehiclesSocketStore((state) => state.loading);
  const isConnected = useVehiclesSocketStore((state) => state.isConnected);
  const initializeWithDevices = useVehiclesSocketStore(
    (state) => state.initializeWithDevices
  );
  const setAwaitingCompanySelection = useVehiclesSocketStore(
    (state) => state.setAwaitingCompanySelection
  );
  const setLoading = useVehiclesSocketStore((state) => state.setLoading);
  const resetStore = useVehiclesSocketStore((state) => state.resetStore);
  const setConnectionStatus = useVehiclesSocketStore(
    (state) => state.setConnectionStatus
  );
  const updateVehiclePosition = useVehiclesSocketStore(
    (state) => state.updateVehiclePosition
  );
  const startTimeTick = useVehiclesSocketStore((state) => state.startTimeTick);
  const stopTimeTick = useVehiclesSocketStore((state) => state.stopTimeTick);
  const finalizeInitialLoad = useVehiclesSocketStore(
    (state) => state.finalizeInitialLoad
  );
  // Necesarios para reflejar `deviceAssignments` (changeDriver / endTrip
  // disparados desde el móvil) sin tener que refrescar /deviceList.
  const updateDriver = useVehiclesSocketStore((state) => state.updateDriver);
  const updateRemolque = useVehiclesSocketStore(
    (state) => state.updateRemolque
  );

  const selectedCompany = useVehiclesSocketStore(
    (state) => state.selectedCompany
  );
  const companyId = useCompaniesStore((state) => state.companyId);
  // groups subscription kept for parity with the socket hook (unused locally)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _groups = useGroupsStore((state) => state.groups);

  const activeFootprintIds = useVehicleFootprintTrackingStore((state) =>
    Object.keys(state.activeFootprints).sort().join(",")
  );
  const addFootprintPoint = useVehicleFootprintTrackingStore(
    (state) => state.addFootprintPoint
  );

  const vehiclesRef = useRef(vehicles);
  vehiclesRef.current = vehicles;

  const hasHandledInit = useRef(false);
  const previousCompanyRef = useRef<string | null>(null);
  const positionsListenerRef = useRef<DatabaseReference | null>(null);
  const tripSnapshotListenerRef = useRef<DatabaseReference | null>(null);
  const tripEventsListenerRef = useRef<DatabaseReference | null>(null);
  // Listener para `deviceAssignments/{companyId}` (driver ↔ vehicle ↔
  // remolque). El backend publica aquí cuando el móvil hace
  // `changeDriver` o `endTrip` para que la card se actualice sin F5.
  const assignmentsListenerRef = useRef<DatabaseReference | null>(null);
  // Listener para `deviceEvents/{companyId}/{deviceId}/{pushId}` — eventos
  // de telemetría (event_code) que el backend (`eventAlertService`) publica
  // cada vez que el device envía una posición con código de evento. Se
  // reenvían al `useDeviceEventsStore` para que la sección Eventos de la
  // VehicleCard los muestre igual que en el flujo socket.
  const deviceEventsListenerRef = useRef<DatabaseReference | null>(null);
  // Última versión recibida por deviceId; descarta writes desordenados.
  const assignmentsVersionRef = useRef<Record<string, number>>({});
  // Conjunto de pushIds de deviceEvents ya entregados al store, para
  // evitar duplicarlos cuando llega de nuevo el snapshot inicial (RTDB
  // entrega el buffer completo en el primer `onValue`).
  const deviceEventsSeenRef = useRef<Set<string>>(new Set());
  const isFirstSnapshot = useRef(true);
  const isFirstDeviceEventsSnapshot = useRef(true);

  const isReseller =
    (localStorage.getItem("userRoleId") || "") === ADMIN_RESELLER_ROLE_ID;

  // ── Subscribe / unsubscribe ──────────────────────────────────────────────

  const unsubscribeFromRdb = useCallback(() => {
    if (positionsListenerRef.current) {
      off(positionsListenerRef.current);
      positionsListenerRef.current = null;
    }
    if (tripSnapshotListenerRef.current) {
      off(tripSnapshotListenerRef.current);
      tripSnapshotListenerRef.current = null;
    }
    if (tripEventsListenerRef.current) {
      off(tripEventsListenerRef.current);
      tripEventsListenerRef.current = null;
    }
    if (assignmentsListenerRef.current) {
      off(assignmentsListenerRef.current);
      assignmentsListenerRef.current = null;
    }
    if (deviceEventsListenerRef.current) {
      off(deviceEventsListenerRef.current);
      deviceEventsListenerRef.current = null;
    }
    assignmentsVersionRef.current = {};
    deviceEventsSeenRef.current = new Set();
    isFirstDeviceEventsSnapshot.current = true;
    stopTimeTick();
    setConnectionStatus(false);
  }, [stopTimeTick, setConnectionStatus]);

  const subscribeToRdb = useCallback(
    (targetCompanyId: string) => {
      unsubscribeFromRdb();

      // 1) Positions
      const positionsRef = ref(database, `positions/${targetCompanyId}`);
      positionsListenerRef.current = positionsRef;
      isFirstSnapshot.current = true;

      startTimeTick();
      setConnectionStatus(true);

      onValue(
        positionsRef,
        (snapshot) => {
          const data = snapshot.val();

          if (isFirstSnapshot.current) {
            isFirstSnapshot.current = false;
            const initialPositions = new Map<string, PositionFromSocket>();

            if (data) {
              Object.keys(data).forEach((deviceId) => {
                const pos: RdbPosition = data[deviceId];
                if (pos && pos.lat != null && pos.lon != null) {
                  initialPositions.set(deviceId, rdbPositionToSocketFormat(pos));
                }
              });
            }

            finalizeInitialLoad(initialPositions);
            return;
          }

          if (!data) return;

          Object.keys(data).forEach((deviceId) => {
            const pos: RdbPosition = data[deviceId];
            if (pos && pos.lat != null && pos.lon != null) {
              updateVehiclePosition(rdbPositionToSocketFormat(pos));
            }
          });
        },
        (error) => {
          console.error(
            "[RDB] Error en suscripción a positions:",
            error.message,
            (error as Error & { code?: string }).code
          );
          if (isFirstSnapshot.current) {
            isFirstSnapshot.current = false;
            finalizeInitialLoad(new Map());
          }
          setConnectionStatus(false);
        }
      );

      // 2) Trip snapshot — tripTracking/{companyId}/{deviceId} → DeviceTripState
      const tripRef = ref(database, `tripTracking/${targetCompanyId}`);
      tripSnapshotListenerRef.current = tripRef;

      onValue(
        tripRef,
        (snapshot) => {
          const data = snapshot.val();
          if (!data) return;
          const updateFromEvent = useTripTrackingStore.getState().updateFromEvent;
          Object.keys(data).forEach((deviceId) => {
            const event = data[deviceId] as TripTrackingEvent | null;
            if (event && event.device_id) updateFromEvent(event);
          });
        },
        (error) => {
          console.error(
            "[RDB] Error en suscripción a tripTracking:",
            error.message
          );
        }
      );

      // 3) Trip events history (cap 50) — tripTrackingEvents/{companyId}/{deviceId}/{pushId}
      const tripEventsBaseRef = ref(
        database,
        `tripTrackingEvents/${targetCompanyId}`
      );
      tripEventsListenerRef.current = tripEventsBaseRef;
      const tripEventsQuery = query(tripEventsBaseRef, limitToLast(50));

      onValue(
        tripEventsQuery,
        (snapshot) => {
          const data = snapshot.val();
          if (!data) return;
          const updateFromEvent = useTripTrackingStore.getState().updateFromEvent;
          // Structure: { deviceId: { pushId: event } }
          Object.keys(data).forEach((deviceId) => {
            const eventsByPushId = data[deviceId];
            if (!eventsByPushId) return;
            // Sort by RTDB key (chronological)
            const events = Object.keys(eventsByPushId)
              .sort()
              .map((k) => eventsByPushId[k] as TripTrackingEvent);
            events.forEach((evt) => {
              if (evt && evt.device_id) updateFromEvent(evt);
            });
          });
        },
        (error) => {
          console.error(
            "[RDB] Error en suscripción a tripTrackingEvents:",
            error.message
          );
        }
      );

      // 4) Device assignments — `deviceAssignments/{companyId}/{deviceId}`
      //
      //    El backend publica aquí en `changeDriver` y `endTrip`
      //    (services/deviceAssignmentsService.js). Permite que el
      //    cambio de driver hecho desde la app móvil se vea al instante
      //    en la VehicleCard, sin pegarle de nuevo a /deviceList.
      //
      //    Defensas:
      //    - Si `vehiclesMap` aún no tiene el `deviceId` (assignment
      //      llega antes que la lista inicial), el store ignora el
      //      update silenciosamente — no rompe, sólo se pierde ese tick
      //      hasta el siguiente write o el F5.
      //    - `updatedAt` se usa como versión para descartar writes
      //      desordenados (RTDB no garantiza orden estricto entre
      //      `set()`s rápidos seguidos).
      const assignmentsRef = ref(
        database,
        `deviceAssignments/${targetCompanyId}`
      );
      assignmentsListenerRef.current = assignmentsRef;

      onValue(
        assignmentsRef,
        (snapshot) => {
          const data = snapshot.val();
          if (!data) return;

          Object.keys(data).forEach((deviceId) => {
            const assignment = data[deviceId];
            if (!assignment) return;

            const ver = Number(assignment.updatedAt) || 0;
            const lastVer = assignmentsVersionRef.current[deviceId] || 0;
            if (ver && ver < lastVer) return;
            assignmentsVersionRef.current[deviceId] = ver;

            if (assignment.driver) {
              updateDriver(deviceId, {
                id: assignment.driver.id ?? undefined,
                name: assignment.driver.name || "Sin asignar",
                licenseId: assignment.driver.licenseId || "",
                photo: assignment.driver.photo || "",
                experience: assignment.driver.experience || "",
                phone: assignment.driver.phone || "",
                email: assignment.driver.email ?? undefined,
              });
            }

            if (Object.prototype.hasOwnProperty.call(assignment, "remolque_id")) {
              updateRemolque(deviceId, assignment.remolque_id || null);
            }
          });
        },
        (error) => {
          console.error(
            "[RDB] Error en suscripción a deviceAssignments:",
            error.message
          );
        }
      );

      // 5) Device events — `deviceEvents/{companyId}/{deviceId}/{pushId}`
      //
      //    El backend (services/eventAlertService.js) hace `push()` en este
      //    nodo cada vez que un device reporta una posición con `event_code`.
      //    El nodo se mantiene como ring buffer (50 entradas por device)
      //    desde el server, así que aquí simplemente reproducimos cada
      //    evento al `useDeviceEventsStore` — el mismo store que ya
      //    alimentaba el flujo socket vía el listener "event".
      //
      //    Deduplicación: RTDB entrega el contenido completo en el primer
      //    snapshot y luego cada nuevo `push()` re-entrega el bucket del
      //    device afectado, así que llevamos un Set de pushIds vistos.
      const deviceEventsRef = ref(
        database,
        `deviceEvents/${targetCompanyId}`
      );
      deviceEventsListenerRef.current = deviceEventsRef;
      isFirstDeviceEventsSnapshot.current = true;

      onValue(
        deviceEventsRef,
        (snapshot) => {
          const data = snapshot.val();
          if (!data) return;
          const addEvent = useDeviceEventsStore.getState().addEvent;
          const isFirst = isFirstDeviceEventsSnapshot.current;
          isFirstDeviceEventsSnapshot.current = false;

          Object.keys(data).forEach((deviceId) => {
            const byPushId = data[deviceId];
            if (!byPushId) return;
            // Ordenamos por pushId (cronológico) para que el store
            // mantenga el orden esperado: más reciente primero.
            const pushIds = Object.keys(byPushId).sort();
            pushIds.forEach((pid) => {
              const evt = byPushId[pid] as RdbDeviceEvent | null;
              if (!evt || !evt.device_id) return;
              const dedupKey = `${deviceId}:${pid}`;
              if (deviceEventsSeenRef.current.has(dedupKey)) return;
              deviceEventsSeenRef.current.add(dedupKey);
              // En el primer snapshot evitamos disparar 50 toasts de golpe:
              // simplemente cargamos los eventos al store sin notificar.
              // El store igual los almacena para que la sección Eventos los
              // muestre apenas el usuario abra la card.
              addEvent({
                device_id: evt.device_id,
                company_id: evt.company_id ?? null,
                vehicle_id: evt.vehicle_id ?? null,
                remolque_id: evt.remolque_id ?? null,
                type: evt.type ?? null,
                event_type: evt.event_type ?? null,
                event_code: evt.event_code ?? null,
                lat: evt.lat ?? null,
                lon: evt.lon ?? null,
                timeStamp: evt.timeStamp ?? null,
                gps_sat: evt.gps_sat ?? null,
                engine_status: evt.engine_status ?? null,
                odo_km: evt.odo_km ?? null,
                rssi: evt.rssi ?? null,
                sim: evt.sim ?? null,
              });
            });
            // Mantener el Set acotado: con 50 entradas por device, ~100
            // devices = 5000 keys, perfectamente sostenible. Aun así
            // podamos el set si excede un techo razonable.
            if (deviceEventsSeenRef.current.size > 20000) {
              deviceEventsSeenRef.current = new Set();
            }
          });
          // `isFirst` queda solo como marker; el store ya está cargado.
          void isFirst;
        },
        (error) => {
          console.error(
            "[RDB] Error en suscripción a deviceEvents:",
            error.message
          );
        }
      );
    },
    [
      startTimeTick,
      setConnectionStatus,
      finalizeInitialLoad,
      updateVehiclePosition,
      unsubscribeFromRdb,
      updateDriver,
      updateRemolque,
    ]
  );

  // ── Initialize ────────────────────────────────────────────────────────────

  const initializeAndConnect = useCallback(async () => {
    if (hasHandledInit.current) return;

    if (isReseller && !selectedCompany) {
      const storeState = useVehiclesSocketStore.getState();
      if (!storeState.isInitialized) {
        setAwaitingCompanySelection(true);
      }
      return;
    }

    const targetCompanyId = isReseller ? selectedCompany : companyId;

    if (!isReseller && !companyId) {
      return;
    }

    const storeState = useVehiclesSocketStore.getState();
    if (
      storeState.isInitialized &&
      storeState.lastInitializedCompanyId === targetCompanyId
    ) {
      hasHandledInit.current = true;
      previousCompanyRef.current = targetCompanyId;
      subscribeToRdb(targetCompanyId!);
      return;
    }

    if (
      storeState.isInitialized &&
      storeState.lastInitializedCompanyId !== targetCompanyId
    ) {
      unsubscribeFromRdb();
      useTripTrackingStore.getState().clear();
      resetStore();
    }

    setLoading(true);

    try {
      const devices = await fetchDevicesFromAPI(targetCompanyId || undefined);

      if (hasHandledInit.current) return;
      const currentState = useVehiclesSocketStore.getState();
      if (
        currentState.isInitialized &&
        currentState.lastInitializedCompanyId === targetCompanyId
      ) {
        hasHandledInit.current = true;
        previousCompanyRef.current = targetCompanyId;
        subscribeToRdb(targetCompanyId!);
        return;
      }

      initializeWithDevices(devices);
      useVehiclesSocketStore.setState({
        lastInitializedCompanyId: targetCompanyId,
      });

      if (devices.length === 0) {
        hasHandledInit.current = true;
        previousCompanyRef.current = targetCompanyId;
        setConnectionStatus(true);
        return;
      }

      hasHandledInit.current = true;
      previousCompanyRef.current = targetCompanyId;

      subscribeToRdb(targetCompanyId!);
    } catch (err: unknown) {
      console.error("[RDB] Error initializing:", err);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, selectedCompany, isReseller]);

  // Sync selectedCompany from companiesStore for resellers
  useEffect(() => {
    if (!isReseller) return;
    if (companyId && companyId !== selectedCompany) {
      useVehiclesSocketStore.getState().setSelectedCompany(companyId);
    }
  }, [isReseller, companyId, selectedCompany]);

  // Mount/unmount
  useEffect(() => {
    initializeAndConnect();
    return () => {
      if (hasHandledInit.current) {
        unsubscribeFromRdb();
        hasHandledInit.current = false;
      }
    };
  }, [initializeAndConnect, unsubscribeFromRdb]);

  // Reseller company change
  useEffect(() => {
    if (!isReseller) return;

    if (
      hasHandledInit.current &&
      selectedCompany !== previousCompanyRef.current
    ) {
      unsubscribeFromRdb();
      useTripTrackingStore.getState().clear();
      resetStore();
      hasHandledInit.current = false;
      previousCompanyRef.current = selectedCompany;

      const timeoutId = setTimeout(() => {
        initializeAndConnect();
      }, 50);

      return () => clearTimeout(timeoutId);
    } else if (!hasHandledInit.current && selectedCompany) {
      setAwaitingCompanySelection(false);
      setLoading(true);

      const timeoutId = setTimeout(() => {
        initializeAndConnect();
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  }, [
    selectedCompany,
    isReseller,
    unsubscribeFromRdb,
    initializeAndConnect,
    resetStore,
    setLoading,
    setAwaitingCompanySelection,
  ]);

  // Footprint tracking
  const footprintIdsArray = useMemo(
    () =>
      activeFootprintIds ? activeFootprintIds.split(",").filter(Boolean) : [],
    [activeFootprintIds]
  );

  const trackedFootprintPositions = useVehiclesSocketStore((state) => {
    if (footprintIdsArray.length === 0) return "";
    return footprintIdsArray
      .map((id) => {
        const idx = state.vehiclesMap.get(id);
        if (idx === undefined) return "";
        const v = state.vehicles[idx];
        return `${id}:${v.latitude},${v.longitude}`;
      })
      .join("|");
  });

  useEffect(() => {
    if (footprintIdsArray.length === 0 || !trackedFootprintPositions) return;

    footprintIdsArray.forEach((vehicleId) => {
      const vehicle = vehiclesRef.current.find((v) => v.id === vehicleId);
      if (vehicle && vehicle.latitude && vehicle.longitude) {
        addFootprintPoint(vehicleId, vehicle.latitude, vehicle.longitude);
      }
    });
  }, [trackedFootprintPositions, footprintIdsArray, addFootprintPoint]);

  return {
    vehicles,
    loading,
    isConnected,
  };
};

export default useVehiclesRdbSubscription;
